import type { GetMeUserResponse } from "@nlc/shared-types";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ConfirmationModal } from "@/components/confirmation-modal";
import { toast } from "@/components/toast/toast-queue";
import { usersKeys } from "@/hooks/queryKeys";

import { useDeleteUser } from "../hooks/useDeleteUser";

export default function DeleteUser({
  onClose,
  openModal,
  userdata,
}: {
  onClose: () => void;
  openModal: boolean;
  userdata: GetMeUserResponse | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = userdata?.user;
  const { isPending, mutate } = useDeleteUser(user?.id);
  const [isOpenModal, setIsOpenModal] = useState(false);

  useEffect(() => {
    if (openModal) {
      setIsOpenModal(true);
    }
    if (!isOpenModal) onClose();
  }, [openModal, isOpenModal]);

  if (!userdata || !user) {
    return null;
  }
  const deleteUser = () => {
    mutate(undefined, {
      onError: (error) => {
        setIsOpenModal(false);
        toast.add({
          description: error.message || "Please try again.",
          timeout: 5000,
          title: "Error deleting user",
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: `User ${user.name} was successfully deleted.`,
          timeout: 5000,
          title: "User deleted",
          type: "success",
        });

        navigate({ to: "/users" });

        return queryClient.invalidateQueries({
          queryKey: usersKeys.all,
        });
      },
    });
  };

  return (
    <ConfirmationModal
      confirmButtonText="Delete"
      isConfirmLoading={isPending}
      isOpen={isOpenModal}
      onConfirm={deleteUser}
      onOpenChange={setIsOpenModal}
      text={`Are you sure you want to delete user ${user.name} - ${user.email}?`}
      title={`Delete user: ${user.name}`}
    />
  );
}
